import React, { useState, useCallback, useEffect } from 'react';
import { quoteService } from '../services/api';
import {
  Route, Truck, Fuel, DollarSign, TrendingDown, ShieldAlert, Info, Loader2,
  ChevronDown, Layers, MapPin, Gauge, AlertTriangle, CheckCircle2, XCircle, Wrench,
} from 'lucide-react';

const money = (n) => (n == null || isNaN(Number(n)) ? '—' : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const pct = (n) => (n == null || isNaN(Number(n)) ? '—' : `${(Number(n) * 100).toFixed(1)}%`);
const num = (v) => (v === '' || v == null ? undefined : (isNaN(Number(v)) ? undefined : Number(v)));

// Provenance tag → human label
const PROV_LABELS = {
  google_maps_api: 'GOOGLE MAPS',
  manual_override: 'MANUAL OVERRIDE',
  manual: 'MANUAL',
  manual_price: 'MANUAL',
  eia_api: 'EIA',
  'computed(EIA price × miles ÷ mpg)': 'EIA × ROUTE',
  unavailable_enter_manually: 'UNAVAILABLE — ENTER MANUALLY',
  internal_calculation: 'INTERNAL CALCULATION',
};
const provLabel = (p) => {
  if (!p) return 'INTERNAL CALCULATION';
  if (PROV_LABELS[p]) return PROV_LABELS[p];
  if (p.startsWith('computed')) return 'INTERNAL CALCULATION';
  return p.replace(/_/g, ' ').toUpperCase();
};

const COST_LINE_LABELS = {
  fuel: 'Fuel', driver_pay: 'Driver compensation', equipment_rental: 'Equipment rental',
  trailer_rental: 'Trailer rental', tolls: 'Tolls', maintenance_reserve: 'Maintenance reserve',
  insurance_alloc: 'Insurance allocation', dispatch_fee: 'Dispatch fee', parking: 'Parking',
  lodging: 'Lodging', accessorials: 'Accessorials', misc: 'Miscellaneous',
};

const FIT_STYLE = {
  FIT: { cls: 'bg-green-100 text-green-800 border-green-200', Icon: CheckCircle2, label: 'FIT' },
  FIT_WITH_CAUTION: { cls: 'bg-amber-100 text-amber-800 border-amber-200', Icon: AlertTriangle, label: 'FIT WITH CAUTION' },
  NOT_FIT: { cls: 'bg-rose-100 text-rose-800 border-rose-200', Icon: XCircle, label: 'NOT FIT' },
  INSUFFICIENT_DATA: { cls: 'bg-gray-100 text-gray-600 border-gray-200', Icon: Info, label: 'INSUFFICIENT DATA' },
};
const PARTIAL_STYLE = {
  COMPATIBLE: 'bg-green-100 text-green-800 border-green-200',
  POSSIBLY_COMPATIBLE: 'bg-amber-100 text-amber-800 border-amber-200',
  NOT_COMPATIBLE: 'bg-rose-100 text-rose-800 border-rose-200',
};
const WARN_STYLE = {
  high: 'bg-rose-50 border-rose-200 text-rose-800',
  med: 'bg-amber-50 border-amber-200 text-amber-800',
  low: 'bg-blue-50 border-blue-200 text-blue-800',
};

const NumField = ({ label, value, onChange, testid, placeholder, hint }) => (
  <div className="flex flex-col">
    <label className="text-[11px] text-gray-600 mb-0.5">{label}</label>
    <input type="number" step="any" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
      data-testid={testid} className="w-full py-1.5 px-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500" />
    {hint && <span className="text-[10px] text-gray-400 mt-0.5">{hint}</span>}
  </div>
);
const TextField = ({ label, value, onChange, testid, placeholder }) => (
  <div className="flex flex-col">
    <label className="text-[11px] text-gray-600 mb-0.5">{label}</label>
    <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
      data-testid={testid} className="w-full py-1.5 px-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500" />
  </div>
);
// Location autocomplete — reuses the existing Google Maps integration (no parallel geocoder).
// Debounced suggestions + spelling correction; degrades to plain manual entry on provider error.
const AutocompleteField = ({ label, value, onChange, onSelect, testid, placeholder }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [degraded, setDegraded] = useState(false);
  const skip = React.useRef(false);

  useEffect(() => {
    if (skip.current) { skip.current = false; return; }
    const t = (value || '').trim();
    if (t.length < 3) { setSuggestions([]); setOpen(false); return; }
    let active = true;
    const timer = setTimeout(async () => {
      setBusy(true);
      try {
        const d = await quoteService.placesAutocomplete(t);
        if (!active) return;
        if (d?.ok) { setSuggestions(d.suggestions || []); setOpen((d.suggestions || []).length > 0); setDegraded(false); }
        else { setSuggestions([]); setOpen(false); setDegraded(true); }
      } catch { if (active) { setSuggestions([]); setOpen(false); setDegraded(true); } }
      finally { if (active) setBusy(false); }
    }, 350);
    return () => { active = false; clearTimeout(timer); };
  }, [value]);

  const choose = (s) => {
    skip.current = true;
    onSelect(s.description || s.main || '');
    setSuggestions([]); setOpen(false);
  };

  return (
    <div className="flex flex-col relative">
      <label className="text-[11px] text-gray-600 mb-0.5">{label}</label>
      <div className="relative">
        <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
          onFocus={() => suggestions.length && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          autoComplete="off" data-testid={testid}
          className="w-full py-1.5 px-2 pr-7 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500" />
        {busy && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400 absolute right-2 top-1/2 -translate-y-1/2" />}
      </div>
      {open && suggestions.length > 0 && (
        <ul data-testid={`${testid}-suggestions`} className="absolute z-30 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-56 overflow-auto">
          {suggestions.map((s, i) => (
            <li key={s.place_id || i}>
              <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => choose(s)}
                data-testid={`${testid}-suggestion-${i}`}
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-blue-50 flex items-start gap-2">
                <MapPin className="w-3.5 h-3.5 text-blue-500 mt-0.5 shrink-0" />
                <span><span className="text-gray-900">{s.main || s.description}</span>{s.secondary && <span className="text-gray-500 text-xs"> · {s.secondary}</span>}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {degraded && (value || '').trim().length >= 3 && (
        <span className="text-[10px] text-amber-600 mt-0.5" data-testid={`${testid}-degraded`}>Suggestions unavailable — type the location manually.</span>
      )}
    </div>
  );
};

const SummaryTile = ({ label, value, tone = 'slate', testid }) => {
  const tones = { slate: 'text-gray-900', green: 'text-green-700', rose: 'text-rose-700', blue: 'text-blue-700' };
  return (
    <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 min-w-0">
      <p className="text-[10px] uppercase tracking-wide text-gray-500 truncate">{label}</p>
      <p className={`text-lg font-bold ${tones[tone]}`} data-testid={testid}>{value}</p>
    </div>
  );
};

const EQUIPMENT_TYPES = [
  { value: 'box_truck', label: 'Box truck / straight truck (payload)' },
  { value: 'cargo_van', label: 'Cargo van (payload)' },
  { value: 'tow_vehicle_trailer', label: 'Tow vehicle + trailer' },
];

const emptyForm = {
  origin: '', destination: '',
  equipment_type: '', payload_lb: '', safety_margin_pct: '0.10',
  // tow + trailer
  tow_vehicle: '', trailer: '', tow_limit_lb: '', trailer_tare_lb: '', trailer_gvwr_lb: '', trailer_payload_lb: '',
  trailer_length_in: '', trailer_width_in: '', trailer_height_in: '',
  // freight
  weight_lb: '', length_in: '', width_in: '', height_in: '', hazmat: false,
  // pricing
  offered_rate: '',
  // manual route/cost
  total_miles: '', loaded_miles: '', deadhead_miles: '', duration_minutes: '',
  mpg: '', fuel_price: '', fuel_cost_override: '',
  driver_pay: '', equipment_rental: '', trailer_rental: '', tolls: '', lodging: '', parking: '',
  accessorials: '', misc: '', maintenance_per_mile: '', insurance_per_mile: '', dispatch_pct: '',
  // config
  target_margin: '0.20', floor_margin: '0.10', aggressive_margin: '0.35', floor_rate_per_mile: '',
  // eligibility
  insurance_active: false, authority_active: true, driver_assigned: true, required_docs_complete: true,
  // partial
  partial_enabled: false, p_weight_lb: '', p_hazmat: false,
};

const QuoteOperationalIntelligence = ({ prefill, sourceLabel }) => {
  const [form, setForm] = useState(emptyForm);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [showInputs, setShowInputs] = useState(true);
  const [showCost, setShowCost] = useState(false);
  const [prefilled, setPrefilled] = useState(false);
  const [routeStale, setRouteStale] = useState(false);
  const [acadiaMeta, setAcadiaMeta] = useState(null);
  const [payloadEdit, setPayloadEdit] = useState(false);
  const [payloadVal, setPayloadVal] = useState('');
  const [payloadProv, setPayloadProv] = useState('PHYSICALLY VERIFIED');
  const [payloadSaving, setPayloadSaving] = useState(false);

  const savePayload = async () => {
    if (!payloadVal) return;
    setPayloadSaving(true);
    try {
      await quoteService.updateEquipment('gmc_acadia_denali_2011', {
        field: 'vehicle_payload_lb', value: Number(payloadVal), provenance: payloadProv,
        note: 'Entered via Quote Builder Update Equipment',
      });
      setPayloadEdit(false); setPayloadVal('');
      await applyAcadia(); // re-resolve authoritative record so Quote Builder consumes the new value
    } catch (e) { /* keep form open on error */ }
    finally { setPayloadSaving(false); }
  };

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const setChk = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.checked }));
  // Location change (type or select) — invalidate stale route intelligence for the new route.
  const setLoc = (k) => (v) => { setForm((f) => ({ ...f, [k]: v })); setResult((r) => { if (r) setRouteStale(true); return r; }); };

  useEffect(() => {
    if (prefill && !prefilled) {
      setForm((f) => ({ ...f, ...prefill }));
      setPrefilled(true);
    }
  }, [prefill, prefilled]);

  const applyAcadia = async () => {
    try {
      const d = await quoteService.equipmentRecord('gmc_acadia_denali_2011');
      const eq = d.intelligence_equipment || {};
      const rec = d.record || {};
      setForm((f) => ({
        ...f, equipment_type: 'tow_vehicle_trailer',
        tow_vehicle: eq.tow_vehicle || '2011 GMC Acadia Denali',
        tow_limit_lb: String(eq.tow_limit_lb ?? '5200'),
        mpg: f.mpg || '15',
      }));
      setAcadiaMeta({
        vin: rec.vin,
        tow: rec.max_loaded_trailer_rating_lb,
        payload: rec.vehicle_payload_lb,
      });
    } catch {
      // Authoritative record unavailable — fall back to the known tow rating, still labeled.
      setForm((f) => ({ ...f, equipment_type: 'tow_vehicle_trailer', tow_vehicle: '2011 GMC Acadia Denali', tow_limit_lb: '5200', mpg: f.mpg || '15' }));
      setAcadiaMeta(null);
    }
  };

  const buildPayload = () => {
    const isTow = form.equipment_type === 'tow_vehicle_trailer';
    const equipment = form.equipment_type ? (isTow ? {
      type: 'tow_vehicle_trailer', tow_limit_lb: num(form.tow_limit_lb), trailer_tare_lb: num(form.trailer_tare_lb),
      trailer_gvwr_lb: num(form.trailer_gvwr_lb), trailer_payload_lb: num(form.trailer_payload_lb),
      trailer_length_in: num(form.trailer_length_in), trailer_width_in: num(form.trailer_width_in), trailer_height_in: num(form.trailer_height_in),
      safety_margin_pct: num(form.safety_margin_pct),
    } : {
      type: form.equipment_type, payload_lb: num(form.payload_lb),
      cargo_length_in: num(form.length_in) != null ? undefined : undefined,
    }) : null;
    const manual = {};
    ['total_miles', 'loaded_miles', 'deadhead_miles', 'duration_minutes', 'mpg', 'fuel_price', 'fuel_cost_override',
      'driver_pay', 'equipment_rental', 'trailer_rental', 'tolls', 'lodging', 'parking', 'accessorials', 'misc',
      'maintenance_per_mile', 'insurance_per_mile', 'dispatch_pct'].forEach((k) => {
      const v = num(form[k]); if (v != null) manual[k] = v;
    });
    const config = {};
    ['target_margin', 'floor_margin', 'aggressive_margin', 'floor_rate_per_mile'].forEach((k) => {
      const v = num(form[k]); if (v != null) config[k] = v;
    });
    const payload = {
      origin: form.origin || null,
      destination: form.destination || null,
      freight: {
        weight_lb: num(form.weight_lb), length_in: num(form.length_in), width_in: num(form.width_in),
        height_in: num(form.height_in), hazmat: form.hazmat,
      },
      equipment,
      offered_rate: num(form.offered_rate),
      manual,
      config,
      eligibility_checks: {
        insurance_active: form.insurance_active, authority_active: form.authority_active,
        driver_assigned: form.driver_assigned, required_docs_complete: form.required_docs_complete,
      },
    };
    if (form.partial_enabled) {
      payload.partial_load = { weight_lb: num(form.p_weight_lb), hazmat: form.p_hazmat };
    }
    return payload;
  };

  const evaluate = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await quoteService.evaluateIntelligence(buildPayload());
      setResult(r); setShowInputs(false); setRouteStale(false);
    } catch (e) {
      setErr(e?.response?.status === 403 ? 'Quote Builder is not active (quote_builder_enabled).' : (e?.response?.data?.detail || 'Evaluation failed.'));
    } finally { setLoading(false); }
  }, [form]);

  const isTow = form.equipment_type === 'tow_vehicle_trailer';
  const r = result;
  const pricing = r?.pricing || {};
  const bands = pricing.price_bands || {};
  const fit = r?.equipment_fit || {};
  const fitStyle = FIT_STYLE[fit.result] || FIT_STYLE.INSUFFICIENT_DATA;
  const elig = r?.operational_eligibility || {};
  const cap = fit.capacity || null;
  const applyBand = (v) => set('offered_rate')(v != null ? String(Math.round(v * 100) / 100) : '');

  return (
    <div className="border-2 border-blue-200 rounded-xl overflow-hidden" data-testid="qb-intelligence">
      <div className="flex items-center justify-between px-4 py-3 bg-blue-900 text-white">
        <span className="flex items-center gap-2 text-sm font-semibold"><Route className="w-4 h-4" /> Operational Intelligence
          <span className="text-[10px] font-normal bg-white/15 rounded px-1.5 py-0.5">ROUTE · FIT · COST · PRICING</span>
        </span>
        {sourceLabel && <span className="text-[11px] bg-emerald-500/20 text-emerald-100 border border-emerald-300/30 rounded px-2 py-0.5" data-testid="qb-intel-source">{sourceLabel}</span>}
      </div>

      <div className="p-4 space-y-4 bg-white">
        {/* ---------- INPUTS ---------- */}
        <div className="border border-gray-200 rounded-lg">
          <button type="button" onClick={() => setShowInputs((s) => !s)} data-testid="qb-intel-inputs-toggle"
            className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 text-sm font-semibold text-gray-800">
            <span className="flex items-center gap-2"><MapPin className="w-4 h-4 text-blue-600" /> Shipment, Equipment & Cost Inputs</span>
            <ChevronDown className={`w-4 h-4 transition-transform ${showInputs ? 'rotate-180' : ''}`} />
          </button>
          {showInputs && (
            <div className="p-3 space-y-4" data-testid="qb-intel-inputs">
              {/* Route */}
              <div>
                <p className="text-xs font-semibold text-gray-700 mb-1.5 flex items-center gap-1"><Route className="w-3.5 h-3.5" /> Route (Google Maps — or enter miles manually)</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <AutocompleteField label="Origin" value={form.origin} onChange={setLoc('origin')} onSelect={setLoc('origin')} testid="qb-intel-origin" placeholder="Grandville, MI" />
                  <AutocompleteField label="Destination" value={form.destination} onChange={setLoc('destination')} onSelect={setLoc('destination')} testid="qb-intel-destination" placeholder="Findlay, OH" />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
                  <NumField label="Loaded miles (manual)" value={form.loaded_miles} onChange={set('loaded_miles')} testid="qb-intel-loaded-miles" />
                  <NumField label="Deadhead miles" value={form.deadhead_miles} onChange={set('deadhead_miles')} testid="qb-intel-deadhead-miles" />
                  <NumField label="Total miles (override)" value={form.total_miles} onChange={set('total_miles')} testid="qb-intel-total-miles" />
                  <NumField label="Drive time (min)" value={form.duration_minutes} onChange={set('duration_minutes')} testid="qb-intel-duration" />
                </div>
              </div>

              {/* Equipment */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-xs font-semibold text-gray-700 flex items-center gap-1"><Truck className="w-3.5 h-3.5" /> Equipment</p>
                  <button type="button" onClick={applyAcadia} data-testid="qb-intel-acadia-preset" className="text-[11px] text-blue-700 hover:underline">Use 2011 GMC Acadia Denali (5,200 lb tow)</button>
                </div>
                {acadiaMeta && (
                  <div className="mb-2 text-[11px] bg-blue-50 border border-blue-100 rounded px-2 py-1.5 space-y-0.5" data-testid="qb-intel-acadia-provenance">
                    <p className="text-blue-900">Loaded from authoritative equipment record {acadiaMeta.vin ? `(VIN ${acadiaMeta.vin})` : ''}.</p>
                    <p className="text-gray-600">Tow rating {acadiaMeta.tow?.value ?? 5200} lb — <b>{acadiaMeta.tow?.provenance || 'MANUFACTURER / VIN-DERIVED'}</b>. This is the max loaded-trailer rating, <b>not</b> freight payload.</p>
                    <p className="text-amber-700">Vehicle payload — <b>{acadiaMeta.payload?.provenance || 'UNKNOWN / PENDING VERIFICATION'}</b>{acadiaMeta.payload?.value ? `: ${acadiaMeta.payload.value} lb` : ' (needs the Tire & Loading label; not invented).'}</p>
                    {(!acadiaMeta.payload?.value) && !payloadEdit && (
                      <button type="button" onClick={() => setPayloadEdit(true)} data-testid="qb-intel-update-equipment"
                        className="mt-1 inline-flex items-center gap-1 text-blue-700 hover:underline font-medium">Update Equipment →</button>
                    )}
                    {payloadEdit && (
                      <div className="mt-2 flex flex-wrap items-end gap-2 bg-white border border-blue-200 rounded p-2" data-testid="qb-intel-payload-form">
                        <div><span className="block text-[10px] text-gray-500">Verified payload (lb)</span>
                          <input type="number" value={payloadVal} onChange={(e) => setPayloadVal(e.target.value)} data-testid="qb-intel-payload-value"
                            className="w-28 border border-gray-300 rounded px-2 py-1 text-sm" placeholder="e.g. 1300" /></div>
                        <div><span className="block text-[10px] text-gray-500">Evidence</span>
                          <select value={payloadProv} onChange={(e) => setPayloadProv(e.target.value)} data-testid="qb-intel-payload-provenance"
                            className="border border-gray-300 rounded px-2 py-1 text-sm">
                            <option>PHYSICALLY VERIFIED</option>
                            <option>FOUNDER / OPERATIONAL OBSERVATION</option>
                          </select></div>
                        <button type="button" onClick={savePayload} disabled={payloadSaving || !payloadVal} data-testid="qb-intel-payload-save"
                          className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50">{payloadSaving ? 'Saving…' : 'Save'}</button>
                        <button type="button" onClick={() => setPayloadEdit(false)} className="px-2 py-1 text-gray-500 text-sm">Cancel</button>
                        <p className="w-full text-[10px] text-gray-400">Enter only from an authoritative vehicle-specific source (Tire &amp; Loading label). Saved to the authoritative equipment record with provenance + audit; Quote Builder resolves it automatically.</p>
                      </div>
                    )}
                  </div>
                )}
                <select value={form.equipment_type} onChange={(e) => set('equipment_type')(e.target.value)} data-testid="qb-intel-equipment-type"
                  className="w-full py-1.5 px-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500">
                  <option value="">— Select equipment —</option>
                  {EQUIPMENT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
                {isTow ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2" data-testid="qb-intel-tow-fields">
                    <TextField label="Tow vehicle" value={form.tow_vehicle} onChange={set('tow_vehicle')} testid="qb-intel-tow-vehicle" />
                    <TextField label="Trailer" value={form.trailer} onChange={set('trailer')} testid="qb-intel-trailer" />
                    <NumField label="Tow limit (lb)" value={form.tow_limit_lb} onChange={set('tow_limit_lb')} testid="qb-intel-tow-limit" hint="Loaded-trailer rating" />
                    <NumField label="Trailer tare (lb)" value={form.trailer_tare_lb} onChange={set('trailer_tare_lb')} testid="qb-intel-trailer-tare" />
                    <NumField label="Trailer GVWR (lb)" value={form.trailer_gvwr_lb} onChange={set('trailer_gvwr_lb')} testid="qb-intel-trailer-gvwr" />
                    <NumField label="Trailer rated payload (lb)" value={form.trailer_payload_lb} onChange={set('trailer_payload_lb')} testid="qb-intel-trailer-payload" />
                    <NumField label="Safety margin (0–0.9)" value={form.safety_margin_pct} onChange={set('safety_margin_pct')} testid="qb-intel-safety-margin" />
                    <NumField label="Trailer length (in)" value={form.trailer_length_in} onChange={set('trailer_length_in')} testid="qb-intel-trailer-length" />
                    <NumField label="Trailer width (in)" value={form.trailer_width_in} onChange={set('trailer_width_in')} testid="qb-intel-trailer-width" />
                  </div>
                ) : form.equipment_type ? (
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <NumField label="Payload capacity (lb)" value={form.payload_lb} onChange={set('payload_lb')} testid="qb-intel-payload" />
                  </div>
                ) : null}
              </div>

              {/* Freight */}
              <div>
                <p className="text-xs font-semibold text-gray-700 mb-1.5 flex items-center gap-1"><Layers className="w-3.5 h-3.5" /> Freight</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <NumField label="Weight (lb)" value={form.weight_lb} onChange={set('weight_lb')} testid="qb-intel-weight" />
                  <NumField label="Length (in)" value={form.length_in} onChange={set('length_in')} testid="qb-intel-length" />
                  <NumField label="Width (in)" value={form.width_in} onChange={set('width_in')} testid="qb-intel-width" />
                  <NumField label="Height (in)" value={form.height_in} onChange={set('height_in')} testid="qb-intel-height" />
                </div>
                <label className="flex items-center gap-1.5 text-xs text-gray-700 mt-1.5"><input type="checkbox" checked={form.hazmat} onChange={setChk('hazmat')} data-testid="qb-intel-hazmat" /> Hazmat</label>
              </div>

              {/* Pricing + fuel */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <NumField label="Customer / offered price ($)" value={form.offered_rate} onChange={set('offered_rate')} testid="qb-intel-offered-rate" />
                <NumField label="MPG" value={form.mpg} onChange={set('mpg')} testid="qb-intel-mpg" />
                <NumField label="Fuel $/gal (manual)" value={form.fuel_price} onChange={set('fuel_price')} testid="qb-intel-fuel-price" hint="Blank = EIA" />
                <NumField label="Fuel cost override ($)" value={form.fuel_cost_override} onChange={set('fuel_cost_override')} testid="qb-intel-fuel-override" />
              </div>

              {/* Manual cost lines (collapsible) */}
              <div className="border border-gray-100 rounded">
                <button type="button" onClick={() => setShowCost((s) => !s)} data-testid="qb-intel-cost-toggle" className="w-full flex items-center justify-between px-2.5 py-1.5 text-xs font-semibold text-gray-700 bg-gray-50">
                  <span className="flex items-center gap-1"><Wrench className="w-3.5 h-3.5" /> Manual cost lines (optional)</span>
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showCost ? 'rotate-180' : ''}`} />
                </button>
                {showCost && (
                  <div className="p-2 grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="qb-intel-cost-lines">
                    <NumField label="Driver comp ($)" value={form.driver_pay} onChange={set('driver_pay')} testid="qb-intel-driver-pay" />
                    <NumField label="Equipment rental ($)" value={form.equipment_rental} onChange={set('equipment_rental')} testid="qb-intel-equipment-rental" />
                    <NumField label="Trailer rental ($)" value={form.trailer_rental} onChange={set('trailer_rental')} testid="qb-intel-trailer-rental" />
                    <NumField label="Tolls ($)" value={form.tolls} onChange={set('tolls')} testid="qb-intel-tolls" />
                    <NumField label="Lodging ($)" value={form.lodging} onChange={set('lodging')} testid="qb-intel-lodging" />
                    <NumField label="Parking ($)" value={form.parking} onChange={set('parking')} testid="qb-intel-parking" />
                    <NumField label="Accessorials ($)" value={form.accessorials} onChange={set('accessorials')} testid="qb-intel-accessorials" />
                    <NumField label="Misc ($)" value={form.misc} onChange={set('misc')} testid="qb-intel-misc" />
                    <NumField label="Maint. $/mi" value={form.maintenance_per_mile} onChange={set('maintenance_per_mile')} testid="qb-intel-maint-permile" />
                    <NumField label="Insurance $/mi" value={form.insurance_per_mile} onChange={set('insurance_per_mile')} testid="qb-intel-ins-permile" />
                    <NumField label="Dispatch % (0–1)" value={form.dispatch_pct} onChange={set('dispatch_pct')} testid="qb-intel-dispatch-pct" />
                  </div>
                )}
              </div>

              {/* Config + eligibility + partial */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <NumField label="Target margin" value={form.target_margin} onChange={set('target_margin')} testid="qb-intel-target-margin" />
                <NumField label="Floor margin" value={form.floor_margin} onChange={set('floor_margin')} testid="qb-intel-floor-margin" />
                <NumField label="Aggressive margin" value={form.aggressive_margin} onChange={set('aggressive_margin')} testid="qb-intel-aggressive-margin" />
                <NumField label="Floor $/mile" value={form.floor_rate_per_mile} onChange={set('floor_rate_per_mile')} testid="qb-intel-floor-rpm" />
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-gray-700">
                <span className="font-semibold text-gray-600">Eligibility:</span>
                <label className="flex items-center gap-1"><input type="checkbox" checked={form.insurance_active} onChange={setChk('insurance_active')} data-testid="qb-intel-insurance-active" /> Insurance active</label>
                <label className="flex items-center gap-1"><input type="checkbox" checked={form.authority_active} onChange={setChk('authority_active')} data-testid="qb-intel-authority-active" /> Authority active</label>
                <label className="flex items-center gap-1"><input type="checkbox" checked={form.driver_assigned} onChange={setChk('driver_assigned')} data-testid="qb-intel-driver-assigned" /> Driver assigned</label>
                <label className="flex items-center gap-1"><input type="checkbox" checked={form.required_docs_complete} onChange={setChk('required_docs_complete')} data-testid="qb-intel-docs-complete" /> Docs complete</label>
              </div>
              <div className="border border-gray-100 rounded p-2 space-y-2">
                <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-700"><input type="checkbox" checked={form.partial_enabled} onChange={setChk('partial_enabled')} data-testid="qb-intel-partial-toggle" /> Test partial-load stacking (2nd load)</label>
                {form.partial_enabled && (
                  <div className="grid grid-cols-2 gap-2">
                    <NumField label="2nd load weight (lb)" value={form.p_weight_lb} onChange={set('p_weight_lb')} testid="qb-intel-partial-weight" />
                    <label className="flex items-center gap-1 text-xs text-gray-700 mt-4"><input type="checkbox" checked={form.p_hazmat} onChange={setChk('p_hazmat')} data-testid="qb-intel-partial-hazmat" /> 2nd load hazmat</label>
                  </div>
                )}
              </div>

              <button type="button" onClick={evaluate} disabled={loading} data-testid="qb-intel-evaluate"
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gauge className="w-4 h-4" />} Evaluate
              </button>
              {err && <p role="alert" className="text-xs text-rose-600" data-testid="qb-intel-error">{err}</p>}
            </div>
          )}
        </div>

        {/* ---------- RESULTS ---------- */}
        {r && (
          <div className="space-y-4" data-testid="qb-intel-result">
            {/* Stale route banner — origin/destination changed since this was calculated */}
            {routeStale && (
              <div className="text-xs bg-amber-50 border border-amber-300 text-amber-900 rounded px-3 py-2 flex items-center justify-between gap-2" data-testid="qb-intel-route-stale">
                <span><b>Route changed.</b> Mileage, drive time, fuel and cost below are for the PREVIOUS origin/destination and are now stale.</span>
                <button type="button" onClick={evaluate} disabled={loading} data-testid="qb-intel-recalc-stale" className="px-2 py-1 bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50 shrink-0">Recalculate</button>
              </div>
            )}
            {/* Degraded banner */}
            {(r.degraded_integrations || []).length > 0 && (
              <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded px-3 py-2" data-testid="qb-intel-degraded">
                <b>Degraded mode:</b> {r.degraded_integrations.join(', ')} unavailable. Enter the affected values manually — the quote is preserved.
              </div>
            )}

            {/* Summary */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">Summary</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <SummaryTile label="Customer / Offered" value={money(pricing.revenue)} tone="blue" testid="qb-intel-sum-revenue" />
                <SummaryTile label="Estimated BlueLock cost" value={money(pricing.estimated_cost)} testid="qb-intel-sum-cost" />
                <SummaryTile label="Estimated profit" value={money(pricing.estimated_profit)} tone={pricing.estimated_profit != null && pricing.estimated_profit < 0 ? 'rose' : 'green'} testid="qb-intel-sum-profit" />
                <SummaryTile label="Estimated margin" value={pct(pricing.margin_pct)} tone={pricing.margin_pct != null && pricing.margin_pct < 0 ? 'rose' : 'slate'} testid="qb-intel-sum-margin" />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2 text-xs">
                <div className="rounded border border-gray-100 px-2 py-1.5"><p className="text-gray-500">Loaded / Deadhead / Total mi</p><p className="font-semibold text-gray-800" data-testid="qb-intel-miles">{r.route?.loaded_miles ?? '—'} / {r.route?.deadhead_miles ?? '—'} / {r.route?.total_miles ?? '—'}</p></div>
                <div className="rounded border border-gray-100 px-2 py-1.5"><p className="text-gray-500">Drive time</p><p className="font-semibold text-gray-800" data-testid="qb-intel-drivetime">{r.route?.duration_minutes != null ? `${Math.round(r.route.duration_minutes)} min` : '—'}</p></div>
                <div className="rounded border border-gray-100 px-2 py-1.5"><p className="text-gray-500">Rev / loaded mi · total mi</p><p className="font-semibold text-gray-800" data-testid="qb-intel-rev-permile">{money(pricing.revenue_per_loaded_mile)} · {money(pricing.revenue_per_total_mile)}</p></div>
                <div className="rounded border border-gray-100 px-2 py-1.5"><p className="text-gray-500">Cost / Profit per total mi</p><p className="font-semibold text-gray-800" data-testid="qb-intel-cost-permile">{money(pricing.cost_per_total_mile)} · {money(pricing.profit_per_total_mile)}</p></div>
              </div>
              <p className="text-[10px] text-gray-400 mt-1">Mileage source: <b>{provLabel(r.provenance?.mileage)}</b> · Fuel source: <b>{provLabel(r.provenance?.fuel)}</b></p>
              {pricing.estimated_cost == null && (
                <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mt-1.5" data-testid="qb-intel-cost-hint">
                  Enter <b>MPG</b> (for fuel from the fetched price × miles) or at least one <b>manual cost line</b> (driver comp, rental, tolls…) to compute Estimated Cost, Profit, Margin and the Pricing Bands.
                </p>
              )}
            </div>

            {/* Pricing bands */}
            <div className="rounded-lg border border-slate-300 bg-slate-50 p-3" data-testid="qb-intel-pricing">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-600 flex items-center gap-1"><DollarSign className="w-3.5 h-3.5" /> Pricing bands</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-700" data-testid="qb-intel-pricing-basis">{bands.note || 'INTERNAL COST-BASED ESTIMATE — not live market data.'}</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {[['floor', 'Floor', 'rose'], ['recommended', 'Recommended', 'slate'], ['aggressive', 'Aggressive / market-high', 'green']].map(([k, lbl, tone]) => (
                  <div key={k} className="rounded border border-slate-200 bg-white px-2 py-2 text-center">
                    <p className="text-[10px] uppercase text-slate-500">{lbl}</p>
                    <p className={`text-base font-bold ${tone === 'rose' ? 'text-rose-700' : tone === 'green' ? 'text-green-700' : 'text-slate-900'}`} data-testid={`qb-intel-band-${k}`}>{money(bands[k])}</p>
                    {bands[k] != null && <button type="button" onClick={() => applyBand(bands[k])} data-testid={`qb-intel-use-${k}`} className="text-[10px] text-blue-600 hover:underline mt-0.5">Use as price</button>}
                  </div>
                ))}
              </div>
              <div className="mt-2 flex items-end gap-2 flex-wrap">
                <NumField label="Founder customer price override ($)" value={form.offered_rate} onChange={set('offered_rate')} testid="qb-intel-price-override" />
                <button type="button" onClick={evaluate} disabled={loading} data-testid="qb-intel-recompute" className="px-3 py-1.5 text-xs bg-slate-700 text-white rounded hover:bg-slate-800 disabled:opacity-50">Recompute with override</button>
                <span className="text-[10px] text-slate-500">Override is authoritative & labeled MANUAL OVERRIDE — source band values are never overwritten.</span>
              </div>
            </div>

            {/* Equipment fit */}
            <div className="rounded-lg border border-gray-200 p-3" data-testid="qb-intel-fit">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-600 flex items-center gap-1"><Truck className="w-3.5 h-3.5" /> Equipment fit</span>
                <span className={`text-[11px] px-2 py-0.5 rounded-full border font-semibold flex items-center gap-1 ${fitStyle.cls}`} data-testid="qb-intel-fit-result"><fitStyle.Icon className="w-3 h-3" /> {fitStyle.label}</span>
              </div>
              {(fit.reasons || []).map((rs, i) => <p key={i} className="text-xs text-gray-700" data-testid={`qb-intel-fit-reason-${i}`}>• {rs}</p>)}
              {cap && (
                <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-1.5 text-[11px]" data-testid="qb-intel-capacity">
                  {form.tow_vehicle && <div className="rounded bg-gray-50 px-2 py-1"><span className="text-gray-500">Tow vehicle</span><br /><b>{form.tow_vehicle}</b></div>}
                  {form.trailer && <div className="rounded bg-gray-50 px-2 py-1"><span className="text-gray-500">Trailer</span><br /><b>{form.trailer}</b></div>}
                  <div className="rounded bg-gray-50 px-2 py-1"><span className="text-gray-500">Tow limit</span><br /><b>{form.tow_limit_lb ? `${form.tow_limit_lb} lb` : '—'}</b></div>
                  <div className="rounded bg-gray-50 px-2 py-1"><span className="text-gray-500">Trailer tare</span><br /><b>{form.trailer_tare_lb ? `${form.trailer_tare_lb} lb` : '—'}</b></div>
                  <div className="rounded bg-gray-50 px-2 py-1"><span className="text-gray-500">Trailer GVWR</span><br /><b>{form.trailer_gvwr_lb ? `${form.trailer_gvwr_lb} lb` : '—'}</b></div>
                  <div className="rounded bg-gray-50 px-2 py-1"><span className="text-gray-500">Rated payload</span><br /><b>{form.trailer_payload_lb ? `${form.trailer_payload_lb} lb` : '—'}</b></div>
                  <div className="rounded bg-gray-50 px-2 py-1"><span className="text-gray-500">Safety margin</span><br /><b>{cap.safety_margin_pct != null ? `${Math.round(cap.safety_margin_pct * 100)}%` : '—'}</b></div>
                  <div className="rounded bg-blue-50 px-2 py-1 border border-blue-100"><span className="text-blue-600">Est. usable freight (scenario)</span><br /><b className="text-blue-800" data-testid="qb-intel-usable-capacity">{cap.usable_cargo_lb != null ? `${cap.usable_cargo_lb} lb` : '—'}</b></div>
                  <div className="rounded bg-gray-50 px-2 py-1"><span className="text-gray-500">Binding limit</span><br /><b data-testid="qb-intel-binding">{cap.binding_constraint ? cap.binding_constraint.replace(/_/g, ' ') : '—'}</b></div>
                  {cap.tongue_weight_note && <div className="col-span-2 sm:col-span-3 text-gray-500">{cap.tongue_weight_note}</div>}
                  <div className="col-span-2 sm:col-span-3 text-[10px] text-gray-500" data-testid="qb-intel-capacity-note">Scenario-derived from the entered trailer inputs — constrained by the lowest applicable limit. Not a physically verified vehicle freight capacity; verify against the trailer's actual tare/GVWR/rated payload and the vehicle's Tire &amp; Loading label.</div>
                </div>
              )}
            </div>

            {/* Physical vs Operational */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="qb-intel-status">
              <div className={`rounded-lg border px-3 py-2 ${elig.physically_capable ? 'bg-green-50 border-green-200' : 'bg-rose-50 border-rose-200'}`}>
                <p className="text-[10px] uppercase tracking-wide text-gray-500">Physical capability</p>
                <p className={`text-sm font-bold ${elig.physically_capable ? 'text-green-700' : 'text-rose-700'}`} data-testid="qb-intel-physical">{elig.physically_capable ? 'PHYSICALLY CAPABLE: YES' : 'PHYSICALLY CAPABLE: NO'}</p>
              </div>
              <div className={`rounded-lg border px-3 py-2 ${elig.operationally_eligible ? 'bg-green-50 border-green-200' : 'bg-rose-50 border-rose-200'}`}>
                <p className="text-[10px] uppercase tracking-wide text-gray-500">Operational eligibility</p>
                <p className={`text-sm font-bold ${elig.operationally_eligible ? 'text-green-700' : 'text-rose-700'}`} data-testid="qb-intel-operational">{elig.operationally_eligible ? 'OPERATIONALLY ELIGIBLE: YES' : 'OPERATIONALLY ELIGIBLE: NO'}</p>
                {(elig.blockers || []).length > 0 && <p className="text-[11px] text-rose-700 mt-0.5" data-testid="qb-intel-elig-blockers">Reason: {elig.blockers.join('; ')}</p>}
              </div>
            </div>

            {/* Warnings */}
            {(r.warnings || []).length > 0 && (
              <div className="space-y-1.5" data-testid="qb-intel-warnings">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 flex items-center gap-1"><ShieldAlert className="w-3.5 h-3.5" /> Warnings</p>
                {r.warnings.map((w, i) => (
                  <div key={i} className={`flex items-start gap-2 text-xs border rounded px-2 py-1.5 ${WARN_STYLE[w.level] || WARN_STYLE.low}`} data-testid={`qb-intel-warning-${w.code}`}>
                    {w.code === 'below_cost' || w.code === 'negative_profit' ? <TrendingDown className="w-3.5 h-3.5 mt-0.5 shrink-0" /> : <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />}
                    <span>{w.msg}</span>
                  </div>
                ))}
                <p className="text-[10px] text-gray-400">Warnings inform the Founder — they do not block a manual override (no legal/safety hard stop applies here).</p>
              </div>
            )}

            {/* Cost breakdown */}
            {r.cost?.items && Object.keys(r.cost.items).length > 0 && (
              <details className="rounded-lg border border-gray-200" data-testid="qb-intel-cost-breakdown">
                <summary className="cursor-pointer px-3 py-2 text-xs font-semibold text-gray-700 bg-gray-50 flex items-center gap-1"><DollarSign className="w-3.5 h-3.5" /> Cost breakdown — total {money(r.cost.total_estimated_cost)}</summary>
                <div className="p-3 space-y-1">
                  {Object.entries(r.cost.items).map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between text-xs" data-testid={`qb-intel-cost-line-${k}`}>
                      <span className="text-gray-700">{COST_LINE_LABELS[k] || k.replace(/_/g, ' ')}</span>
                      <span className="flex items-center gap-2">
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 uppercase">{provLabel(r.cost.provenance?.[k])}</span>
                        <b className="text-gray-900">{money(v)}</b>
                      </span>
                    </div>
                  ))}
                </div>
              </details>
            )}

            {/* Partial load */}
            {r.partial_load && (
              <div className="rounded-lg border border-gray-200 p-3" data-testid="qb-intel-partial">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-600 flex items-center gap-1"><Layers className="w-3.5 h-3.5" /> Partial-load stacking</span>
                  <span className={`text-[11px] px-2 py-0.5 rounded-full border font-semibold ${PARTIAL_STYLE[r.partial_load.result] || 'bg-gray-100 text-gray-600 border-gray-200'}`} data-testid="qb-intel-partial-result">{r.partial_load.result?.replace(/_/g, ' ')}</span>
                </div>
                {(r.partial_load.reasons || []).map((rs, i) => <p key={i} className="text-xs text-gray-700">• {rs}</p>)}
                <p className="text-[11px] text-gray-500 mt-1">Combined weight {r.partial_load.combined_weight_lb ?? '—'} lb vs capacity {r.partial_load.capacity_lb ?? '—'} lb. Decision support only — no automatic load combination.</p>
              </div>
            )}

            <p className="text-[10px] text-gray-400" data-testid="qb-intel-note">{r.note}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuoteOperationalIntelligence;
